import React from "react";
import CheckmarkIcon from "../../assets/logo/CheckmarkIcon";
import CloseIcon from "../../assets/logo/CloseIcon";
import useTheme from "../../hooks/useTheme";
import {ORDER_FULFILLMENT_STATUSES, ORDER_METHODS} from "../../services/exports/Constants";
import {
  EventStatusCopyInterface,
  OrderFulfillmentEvent,
  OrderItemInterface,
  ThemeInterface,
} from "../../services/exports/Interfaces";
import {useTranslation} from "react-i18next";
import useAppMethods from "../../hooks/useAppMethods";

interface Props {
  order: OrderItemInterface | null;
}

export default function PickUpProgress(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:PickUp:PickUpProgress' });

  const { order } = props;

  const theme: ThemeInterface = useTheme();
  const { formatTime } = useAppMethods();

  function renderIcon(event: OrderFulfillmentEvent) {
    function getIconBackground() {
      switch (event?.status) {
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return theme.color.brand.danger;
        default:
          return theme.color.brand.primary;
      }
    }

    function getIcon() {
      switch (event?.status) {
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return <CloseIcon color={theme.color.text.light} />;
        default:
          return <CheckmarkIcon color={theme.color.text.light} />;
      }
    }

    return (
      <div
        className={iconContainer}
        style={{ backgroundColor: getIconBackground() }}
      >
        {getIcon()}
      </div>
    );
  }

  function getCopy(event: OrderFulfillmentEvent) {
    switch (event?.status) {
      case ORDER_FULFILLMENT_STATUSES.REJECTED:
        return {
          title: t('status.rejected.title'),
        };
      case ORDER_FULFILLMENT_STATUSES.CREATED:
        return {
          title: t('status.created.title'),
        };
      case ORDER_FULFILLMENT_STATUSES.REVIEWED:
        return {
          title: t('status.reviewed.title'),
        };
      case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        return {
          title: t('status.confirmed.title'),
        };
      default:
        return {
          title: '',
        };
    }
  }

  function getAwaitingOrderConfirmationCopy() {
    return {
      title: t('status.waiting_confirmation.title'),
      description: t('status.waiting_confirmation.description'),
    };
  }

  function getOrderConfirmedCopy() {
    if (order?.method === ORDER_METHODS.DELIVERY) {
      return {
        title: t('status.delivery_time.title'),
        description: t('status.delivery_time.description', {
          name: order?.company?.name,
        }),
      };
    }

    return {
      title: t('status.pickup_time.title'),
      description: t('status.pickup_time.description', {
        name: order?.company?.name,
      }),
    };
  }

  function getInfo(event: OrderFulfillmentEvent) {
    switch (event?.status) {
      case ORDER_FULFILLMENT_STATUSES.CREATED:
        return `#${order?.number}`;
      default:
        return formatTime(event?.created_at);
    }
  }

  const renderEvent = (event: OrderFulfillmentEvent, copy: EventStatusCopyInterface) => (
    <div className={itemContainer}>
      <div className={itemWrapper}>
        {renderIcon(event)}
        <div className={descriptionContainer}>
          <p>{copy?.title}</p>
          {copy?.description && (
            <h5 className={descriptionStyle}>{copy?.description}</h5>
          )}
        </div>
      </div>
      <p className={timeStyle}>{getInfo(event)}</p>
    </div>
  );

  const renderWaitingOrderConfirmation = (
    event: OrderFulfillmentEvent,
    copy: EventStatusCopyInterface
  ) => {
    switch (order?.status) {
      case ORDER_FULFILLMENT_STATUSES.CREATED:
      case ORDER_FULFILLMENT_STATUSES.REVIEWED:
        return (
          <div className={itemContainer}>
            <div className={itemWrapper}>
              <div
                className={iconContainer}
                style={{ backgroundColor: theme.color.brand.inkGrey.grey_2 }}
              >
                <CheckmarkIcon color={theme.color.text.grey} />
              </div>
              <div className={descriptionContainer}>
                <p>{copy?.title}</p>
                {copy?.description && (
                  <h5 className={descriptionStyle}>{copy?.description}</h5>
                )}
              </div>
            </div>
            <p className={timeStyle}>{getInfo(event)}</p>
          </div>
        );
      default:
        return null;
    }
  };

  const renderPickUpTime = (event: OrderFulfillmentEvent, copy: EventStatusCopyInterface) => (
    <div className={itemContainer}>
      <div className={itemWrapper}>
        {renderIcon(event)}
        <div className={descriptionContainer}>
          <p>{copy?.title}</p>
          {copy?.description && (
            <h5 className={descriptionStyle}>{copy?.description}</h5>
          )}
        </div>
      </div>
      <p className={timeStyle} style={{ color: theme.color.brand.primary }}>
        {formatTime(order?.estimated_pickup_time)}
      </p>
    </div>
  );

  function renderItem(event: OrderFulfillmentEvent, index: number) {
    const copyWrite: EventStatusCopyInterface = getCopy(event);
    const awaitingOrderConfirmationCopy: EventStatusCopyInterface =
      getAwaitingOrderConfirmationCopy();
    const orderConfirmedCopy: EventStatusCopyInterface =
      getOrderConfirmedCopy();

    let eventsLength = order?.fulfillment_events?.length ?? 0;
    switch (event.status) {
      case ORDER_FULFILLMENT_STATUSES.REJECTED:
      case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
      case ORDER_FULFILLMENT_STATUSES.REVIEWED:
      case ORDER_FULFILLMENT_STATUSES.CREATED:
        return (
          <div key={event?.id?.toString()}>
            {renderEvent(event, copyWrite)}
            {index === eventsLength - 1 &&
              renderWaitingOrderConfirmation(
                event,
                awaitingOrderConfirmationCopy
              )}
            {event.status === ORDER_FULFILLMENT_STATUSES.ACCEPTED &&
              renderPickUpTime(event, orderConfirmedCopy)}
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div className={container}>
      {order?.fulfillment_events?.map(
        (event: OrderFulfillmentEvent, index: number) => (
          <div key={event?.id?.toString()}>{renderItem(event, index)}</div>
        )
      )}
    </div>
  );
}

const container =
  "w-full bg-background-inkWhite-white_1 shadow rounded-md border border-brand-inkGrey-grey_2";

const iconContainer = "w-8 h-8 rounded-full flex justify-center items-center";

const itemContainer = "flex flex-row justify-between p-small";

const itemWrapper = "flex flex-row";

const descriptionContainer = "px-xsmall flex flex-col flex-1";

const descriptionStyle = "text-brand-text-grey mini mt-tiny";

const timeStyle = "";
